import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import _ from "underscore";
import { apiPromiseHook } from "../../../../shared/flux/actions/enterHooks";
import { SampleItineraryHeader } from "@travellocal/ui";
import { buildMetaTags, buildStructuredDataSnippet } from "../../../../shared/helpers/metaData";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import * as WebRoutes from "@travellocal/utils/webRoutes"
import { ActionPromises } from "../../../state/actionPromises";
import { EnterHookPromises } from "../../../state/enterHookPromises";
import { Loader } from "../../../../core/atoms/atoms";

import "./itinerary.scss";

interface StateProps {
  sampleItinerary: IState<ISampleItinerarySummary>;
  currencyState: IState<IBaseCurrency>;
}

interface DispatchProps {
  navigate: (route: string) => void;
  reloadSampleItinerary: (slug: string, code: string) => Promise<any>;
}

type Props = StateProps & DispatchProps;

interface State {
  reloading: boolean;
}

export class ItineraryIndexComponent extends React.Component<Props, State> {

  public state = {
    reloading: false,
  };

  public componentWillReceiveProps(newProps: Props) {
    if (!_.isEqual(this.props.currencyState.data, newProps.currencyState.data)) {
      this.reload(newProps);
    }

  }

  private reload = async (newProps: Props) => {
    this.setState({ reloading: true });
    try {
      await this.props.reloadSampleItinerary(this.props.sampleItinerary.data.slug, newProps.currencyState.data.code);
    } finally {
      this.setState({ reloading: false });
    }
  }

  private itineraryStructuredData = (sampleItinerary: ISampleItinerarySummary) => {
    const structuredData = {
      name: sampleItinerary.name,
      description: sampleItinerary.description,
      touristType: ["Cultural tourism"],
      subjectOf: {
        "@type": "CreativeWork",
        "name": sampleItinerary.name,
        "url": `${globalConfig.wwwBaseUrl}${WebRoutes.sampleItinerary(sampleItinerary.slug)}`,
      },
      itinerary: {
        "@type": "ItemList",
        "numberOfItems": sampleItinerary.numberOfDays,
        "itemListElement": sampleItinerary.days.map((day, index) => {
          return {
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "TouristAttraction",
              "name": `Day ${index + 1} - ${day.locationNames.join(", ")}`,
              "description": day.events[0]?.description,
            },
          };
        }),
      },
    };
    return buildStructuredDataSnippet("TouristTrip", structuredData);
  }

  private renderOk = () => {
    const itinerary = this.props.sampleItinerary.data;
    const myUrl = `${globalConfig.wwwBaseUrl}${WebRoutes.sampleItinerary(itinerary.slug)}`;

    return (
      <div className="itinerary-view">
        <Helmet
          title={itinerary.titleTag || itinerary.name}
          meta={buildMetaTags(itinerary, myUrl, itinerary.heroImage)}
          script={[this.itineraryStructuredData(itinerary)]}
          link={[
            { rel: "canonical", href: myUrl },
          ]}
        />

        <SampleItineraryHeader itinerary={itinerary} />

        {this.props.children}
      </div>
    );
  }

  private renderError = () => {
    return (
      <div className="itinerary-view width-wrapper m-top-large">
        <h1>Sorry!</h1>
        <p>We can&apos;t show you this sample itinerary right now.</p>
      </div>
    );
  }

  public render() {
    return (
      <div>
        {MatchState(this.props.sampleItinerary, {
          ok: this.renderOk,
          pending: () => {
            if (this.props.sampleItinerary.data != null && this.state.reloading) {
              return this.renderOk();
            }
            return (
              <Loader />
            );
          },
          error: this.renderError,
        })}
      </div>
    );
  }
}

const mapStateToProps = (state: IWebStoreState): StateProps => {
  return {
    sampleItinerary: state.sampleItinerary,
    currencyState: state.currency,
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => {
  return {
    navigate: (route: string) => dispatch(push(route)),
    reloadSampleItinerary: (slug: string, code: string) => {
      return dispatch(ActionPromises.sampleItineraryById(slug, code));
    },
  };
};

export const ItineraryIndex = connect(mapStateToProps, mapDispatchToProps)(ItineraryIndexComponent);

export const itineraryIndexOnEnter = apiPromiseHook(EnterHookPromises.sampleItineraryById("itineraryId"));
